import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Mic, BookOpen, Users, TrendingUp, Globe, Shield, Brain, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CATEGORY_BADGE = {
  "IA y futuro del trabajo": "badge-ia",
  "Empresa y estrategia": "badge-empresa",
  "Fundraising y narrativa": "badge-fundraising",
  "Geopolítica empresarial": "badge-geopolitica",
  "Liderazgo y construcción": "badge-liderazgo",
  "Notas breves": "badge-notas",
};

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("es-ES", { year: "numeric", month: "long", day: "numeric" });
}

const topicAreas = [
  { icon: Brain, label: "IA aplicada a empresa", desc: "Integración operativa, no espectáculo tecnológico. Cómo la IA rediseña decisiones, productividad y organización." },
  { icon: TrendingUp, label: "Liderazgo y estrategia", desc: "Qué exige dirigir cuando el contexto cambia más rápido que las estructuras. Empresa real, no manual de gestión." },
  { icon: Users, label: "Empleo y transformación", desc: "Criterio vs. rutina. El futuro del trabajo no va de sustituir personas, sino de saber cuándo la máquina no puede." },
  { icon: BookOpen, label: "Fundraising y narrativa", desc: "Cómo contar una compañía para atraer capital sin caer en fórmulas vacías. Relato como instrumento estratégico." },
  { icon: Globe, label: "Geopolítica empresarial", desc: "Cómo los cambios regulatorios, energéticos y de poder afectan a las empresas sin que casi nadie lo vea." },
  { icon: Shield, label: "Confianza y modelos duraderos", desc: "La confianza como activo económico. Por qué los mercados saturados se ganan resolviendo problemas, no vendiendo." },
];

const services = [
  { icon: Mic, title: "Ponencias", desc: "Para eventos empresariales, tecnológicos o sectoriales. Temas con tesis propia, no charlas motivacionales." },
  { icon: BookOpen, title: "Clases magistrales", desc: "Para escuelas de negocio y universidades. Empresa real, casos propios, reflexión aplicada." },
  { icon: Users, title: "Workshops para directivos", desc: "Sesiones pequeñas, intensas y muy cualificadas para comités de dirección y equipos en transformación." },
  { icon: Brain, title: "Sesiones estratégicas", desc: "Con CEOs y fundadores. Reflexión sobre modelo, posicionamiento, narrativa y decisiones críticas." },
  { icon: BookOpen, title: "Storytelling corporativo", desc: "Ayuda en tesis de marca, relato fundacional y discurso del CEO. Comunicación que ordena, no que adorna." },
  { icon: TrendingUp, title: "Fundraising narrative", desc: "Preparación estratégica para conversaciones con inversores. Deck, tesis, mensaje y relato de ronda." },
];

function NewsletterBlock() {
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/newsletter/subscribe`, form);
      if (res.data.already_subscribed) toast.info("Ya estás suscrito a la newsletter.");
      else toast.success("Suscripción confirmada. Bienvenido.");
      setForm({ name: "", email: "" });
    } catch {
      toast.error("Algo no funcionó. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} data-testid="newsletter-form-home" className="flex flex-col sm:flex-row gap-3 max-w-lg">
      <input
        className="input-underline flex-1"
        placeholder="Tu nombre"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        required
        data-testid="newsletter-name-input"
      />
      <input
        type="email"
        className="input-underline flex-1"
        placeholder="tu@email.com"
        value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
        required
        data-testid="newsletter-email-input"
      />
      <button
        type="submit"
        disabled={loading}
        data-testid="newsletter-submit-btn"
        className="label-mono bg-primary text-primary-foreground px-7 py-3 hover:bg-primary/90 transition-colors disabled:opacity-60 whitespace-nowrap"
      >
        {loading ? "Enviando…" : "Suscribirme"}
      </button>
    </form>
  );
}

export default function Home() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/posts?limit=3`).then((r) => setPosts(r.data)).catch(() => {});
  }, []);

  return (
    <div className="pt-16 md:pt-20">

      {/* ── HERO ── */}
      <section data-testid="hero-section" className="min-h-[90vh] flex items-center border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 w-full py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="animate-fade-slide-up">
            <p className="label-mono text-primary mb-6">CEO · COFUNDADOR · ROAMS</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-normal leading-[1.08] tracking-tight mb-8 text-foreground">
              Empresa, IA y estrategia desde la experiencia de construir.
            </h1>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-10 max-w-lg">
              Ideas para directivos, fundadores e inversores que prefieren criterio a ruido.
              Escribo sobre empresa, inteligencia artificial, empleo y las fuerzas que están
              reordenando el trabajo y el poder.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/newsletter"
                data-testid="hero-newsletter-cta"
                className="label-mono bg-primary text-primary-foreground px-8 py-4 hover:bg-primary/90 transition-colors text-center"
              >
                Suscribirse a la newsletter
              </Link>
              <Link
                to="/ponencias"
                data-testid="hero-speaking-cta"
                className="label-mono border border-foreground/20 px-8 py-4 hover:border-primary hover:text-primary transition-colors text-center"
              >
                Invitar a una charla
              </Link>
            </div>
          </div>
          {/* Photo */}
          <div className="relative animate-fade-in delay-200 order-first lg:order-last">
            <div className="relative overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_ai-strategy-real/artifacts/hzqpgdt8__DSC4824.jpg"
                alt="Eduardo Delgado Lobejón"
                data-testid="hero-photo"
                className="w-full max-h-[580px] object-cover object-top grayscale-[10%]"
              />
              <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background/30 to-transparent pointer-events-none" />
            </div>
            <div className="absolute -bottom-4 -left-4 bg-card border border-border px-5 py-4 hidden md:block">
              <p className="label-mono text-primary">Premio Pabellón España</p>
              <p className="label-mono text-muted-foreground">MWC 2026 · Barcelona</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── QUÉ ENCONTRARÁS ── */}
      <section data-testid="what-youll-find-section" className="py-24 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            <div>
              <p className="label-mono text-muted-foreground mb-4">Qué encontrarás aquí</p>
              <h2 className="font-serif text-3xl md:text-4xl font-normal mb-6 leading-tight">
                Pensamiento aplicado, no relato de éxito.
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Esta web no funciona como currículum ampliado. Funciona como espacio de ideas.
                Ensayos breves con tesis propias sobre empresa, IA, poder y empleo, escritos
                desde la experiencia de construir una compañía real.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[
                { title: "Ensayos con tesis", desc: "Textos de 700 a 1.500 palabras. Siempre una idea central, nunca relleno." },
                { title: "Reflexiones sobre empresa e IA", desc: "Cómo la inteligencia artificial cambia el trabajo, las decisiones y los modelos de negocio." },
                { title: "Aprendizajes de fundador", desc: "Lo que no se cuenta del crecimiento, el fundraising y operar en sectores regulados." },
                { title: "Ideas para decidir mejor", desc: "Útil para directivos, emprendedores e inversores con criterio propio." },
              ].map((item) => (
                <div key={item.title} className="bg-card p-6 border-l-2 border-primary">
                  <p className="font-medium text-sm mb-2">{item.title}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── AUTORIDAD ── */}
      <section data-testid="authority-section" className="py-24 border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="label-mono text-muted-foreground mb-10">Contexto</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-serif text-3xl md:text-4xl font-normal mb-8 leading-tight">
                No habla de empresa desde la teoría. La construye.
              </h2>
              <ul className="space-y-5">
                {[
                  "CEO y cofundador de Roams — compañía tecnológica en servicios esenciales (telefonía, energía, seguros) con enfoque human-first e IA aplicada a operaciones.",
                  "Premio Pabellón de España en el Mobile World Congress 2026 — reconocimiento a la innovación tecnológica nacional.",
                  "Premio Castilla y León Emprende — reconocimiento al emprendimiento con impacto regional e industrial.",
                  "Ponente en mesas de expertos sobre IA, empresa y futuro del trabajo. Santander, MWC y eventos sectoriales.",
                  "Construcción empresarial desde Palencia con ambición nacional. Fuera de los polos habituales del ecosistema.",
                ].map((fact, i) => (
                  <li key={i} className="flex gap-4 items-start">
                    <span className="font-mono text-primary text-xs mt-1 shrink-0">0{i + 1}</span>
                    <p className="text-sm text-muted-foreground leading-relaxed">{fact}</p>
                  </li>
                ))}
              </ul>
            </div>
            {/* Photos grid */}
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://customer-assets.emergentagent.com/job_ai-strategy-real/artifacts/ch2r0wmu_IMG_0396%202.jpeg"
                alt="Premio MWC 2026"
                className="w-full h-52 object-cover grayscale-[15%]"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_ai-strategy-real/artifacts/kkjmxjy8_7fea03f9-f6d7-4502-a638-0134683785f7.jpg"
                alt="Castilla y León Emprende"
                className="w-full h-52 object-cover grayscale-[15%]"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_ai-strategy-real/artifacts/z7vx5e2w_IMG_0394%202.jpeg"
                alt="MWC Barcelona"
                className="w-full h-52 object-cover col-span-2 grayscale-[15%]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── ÁREAS TEMÁTICAS ── */}
      <section data-testid="topics-section" className="py-24 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="label-mono text-muted-foreground mb-4">Sobre qué escribe</p>
          <h2 className="font-serif text-3xl md:text-4xl font-normal mb-12 max-w-lg leading-tight">
            Seis áreas donde la empresa y la realidad se tocan.
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {topicAreas.map((area) => {
              const Icon = area.icon;
              return (
                <div
                  key={area.label}
                  data-testid={`topic-${area.label}`}
                  className="bg-card p-8 hover:-translate-y-0.5 hover:shadow-sm transition-all duration-300 group"
                >
                  <Icon size={20} className="text-primary mb-5 group-hover:scale-110 transition-transform" />
                  <p className="font-serif text-lg mb-3">{area.label}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">{area.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── SERVICIOS ── */}
      <section data-testid="services-section" className="py-24 border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <p className="label-mono text-muted-foreground mb-4">Colaboraciones</p>
              <h2 className="font-serif text-3xl md:text-4xl font-normal leading-tight max-w-md">
                Seis formas de trabajar juntos.
              </h2>
            </div>
            <Link
              to="/ponencias"
              data-testid="services-view-all"
              className="label-mono text-primary flex items-center gap-2 hover:gap-3 transition-all"
            >
              Ver todos los formatos <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.title} data-testid={`service-${s.title}`} className="bg-background p-8 border border-border hover:border-primary/30 transition-colors">
                  <Icon size={18} className="text-secondary mb-4" />
                  <p className="font-serif text-lg mb-2">{s.title}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── ÚLTIMOS ENSAYOS ── */}
      <section data-testid="recent-posts-section" className="py-24 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <p className="label-mono text-muted-foreground mb-4">Ideas recientes</p>
              <h2 className="font-serif text-3xl md:text-4xl font-normal leading-tight">Últimos ensayos.</h2>
            </div>
            <Link to="/ideas" data-testid="view-all-posts" className="label-mono text-primary flex items-center gap-2 hover:gap-3 transition-all">
              Ver todos <ArrowRight size={14} />
            </Link>
          </div>
          {posts.length === 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map((n) => (
                <div key={n} className="bg-card p-8 h-60 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {posts.map((post) => (
                <Link
                  key={post.id}
                  to={`/ideas/${post.slug}`}
                  data-testid={`post-card-${post.id}`}
                  className="bg-card p-8 hover:-translate-y-0.5 hover:shadow-sm transition-all duration-300 group flex flex-col"
                >
                  <span className={`label-mono text-xs px-2 py-1 self-start mb-5 ${CATEGORY_BADGE[post.category] || "badge-notas"}`}>
                    {post.category}
                  </span>
                  <p className="font-serif text-xl leading-snug mb-3 group-hover:text-primary transition-colors line-clamp-2">{post.title}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed mb-6 line-clamp-3 flex-1">{post.excerpt}</p>
                  <div className="flex items-center justify-between mt-auto">
                    <span className="label-mono text-muted-foreground">{post.read_time} min</span>
                    <ChevronRight size={14} className="text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── NEWSLETTER CTA ── */}
      <section data-testid="newsletter-cta-section" className="py-24 border-b border-border bg-foreground text-background">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="max-w-2xl">
            <p className="label-mono text-background/50 mb-5">Newsletter</p>
            <h2 className="font-serif text-3xl md:text-4xl font-normal mb-4 leading-tight text-background">
              Ensayos breves sobre empresa, IA, empleo, poder y estrategia.
            </h2>
            <p className="text-background/70 leading-relaxed mb-8 text-sm md:text-base">
              Sin ruido. Sin relleno. Una publicación escrita por alguien que dirige, piensa y se moja.
              Frecuencia: semanal o quincenal, según el nivel que merezca.
            </p>
            <NewsletterBlock />
            <p className="text-xs text-background/40 mt-4">Sin spam. Baja cuando quieras.</p>
          </div>
        </div>
      </section>

      {/* ── CONTACTO FINAL ── */}
      <section data-testid="contact-section-home" className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 text-center">
          <p className="label-mono text-muted-foreground mb-5">¿Tienes algo concreto en mente?</p>
          <h2 className="font-serif text-3xl md:text-4xl font-normal mb-6 leading-tight max-w-lg mx-auto">
            Conversaciones de alto valor bienvenidas.
          </h2>
          <p className="text-muted-foreground mb-10 max-w-md mx-auto text-sm md:text-base leading-relaxed">
            Para directivos, fundadores, escuelas de negocio, medios e inversores con un motivo concreto.
          </p>
          <Link
            to="/contacto"
            data-testid="home-contact-cta"
            className="label-mono bg-primary text-primary-foreground px-10 py-4 hover:bg-primary/90 transition-colors inline-block"
          >
            Iniciar conversación
          </Link>
        </div>
      </section>
    </div>
  );
}
